import { HTMLAttributes } from 'react'
import lowerCase from 'lodash/lowerCase'
import Link from 'next/link'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import SVGIcon from '@/components/base/SVGIcon'
import Symbol from '@/components/base/Symbol'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'

export type FooterProps = HTMLAttributes<HTMLElement>

const Footer = ({ className, ...props }: FooterProps) => {
  const { menu, strings } = useStore()

  return (
    <footer
      className={cn(['relative z-10 bg-black text-white', className])}
      {...props}
    >
      <Container className="relative z-10 py-[3rem] md:py-[6rem]">
        <Grid>
          <div
            className={cn(
              'col-span-4 flex flex-col items-center justify-between gap-y-[3rem] md:col-span-6 md:flex-row md:items-start md:gap-y-0 lg:col-span-12'
            )}
          >
            {/* First Column */}
            <Link href="/" scroll={false}>
              <Symbol data-symbol className="size-[4.8rem]" />
            </Link>
            {/* Second Column */}
            <div className="flex w-auto flex-col space-y-[1rem] text-center md:text-left lg:w-[20.5rem]">
              <Typography variant="label">{strings?.address_title}</Typography>
              <Typography
                variant="small"
                paragraph
                className={cn(['leading-[2.5rem] text-white/60'])}
              >
                {strings?.address_text}
              </Typography>
            </div>
            {/* Third Column */}
            <div className="flex w-auto flex-col space-y-[1rem] text-center md:text-left lg:w-[20.5rem]">
              <Typography variant="label">
                {strings?.footer_menu_title}
              </Typography>
              <ul className="flex flex-col">
                {menu?.legals_menu?.menu.map(
                  ({ url, title, target, classes }, index) => (
                    <li key={`${classes}-${index}`}>
                      <Link
                        href={url}
                        target={target}
                        className={cn([
                          '-m-[1rem] inline-flex space-x-[1rem] p-[1rem]',
                          'transition-colors duration-300 ease-out-cubic',
                          'text-white/60 hover:text-white/80'
                        ])}
                        scroll={false}
                      >
                        <Typography variant="small">{title}</Typography>
                      </Link>
                    </li>
                  )
                )}
              </ul>
              <ul className="!mt-[2.4rem] flex gap-[1.6rem]">
                {menu?.social_menu?.menu?.map(
                  ({ url, title, target, classes }, index) => (
                    <li key={`${classes}-${index}`}>
                      <a
                        href={url}
                        target={target}
                        className={cn([
                          'flex h-[1.4rem] w-auto items-center justify-center',
                          'group'
                        ])}
                      >
                        <SVGIcon
                          symbol={`${lowerCase(title)}-icon`}
                          className={cn([
                            'h-full w-full shrink-0 fill-dark-300',
                            'transition-colors duration-300 ease-out-cubic group-hover:fill-white'
                          ])}
                        />
                      </a>
                    </li>
                  )
                )}
              </ul>
            </div>
            {/* Fourth Column */}
            <div className="flex w-[26.5rem] flex-col space-y-[1rem]">
              {strings?.newsletter_button?.url ? (
                <Button variant="dark" asChild>
                  <Link
                    href={strings.newsletter_button.url}
                    target={strings.newsletter_button.target}
                    scroll={false}
                  >
                    <span
                      className="after:!text-small after:!normal-case"
                      data-label={strings.newsletter_button.title}
                    >
                      <Typography variant="small">
                        {strings.newsletter_button.title}
                      </Typography>
                    </span>
                    <SVGIcon
                      symbol="arrow-right-icon"
                      className="h-[1.1rem] w-[1.6rem] shrink-0 fill-current"
                    />
                  </Link>
                </Button>
              ) : null}
            </div>
          </div>
        </Grid>
      </Container>
    </footer>
  )
}

export default Footer
